// @ts-nocheck
import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { adminApi } from '@/api/adminClient'
import { useQueryClient } from '@tanstack/react-query'

interface AdminUser {
  id: string
  email: string
  fullName: string
  isActive: boolean
  twoFaEnabled: boolean
  lastLoginAt: string | null
  createdAt: string
}

interface AdminAuthContextValue {
  admin: AdminUser | null
  loading: boolean
  login: (email: string, password: string) => Promise<{ requiresTwoFa: boolean; twoFaSetupRequired?: boolean }>
  setup2FA: () => Promise<{ secret: string; qrCodeUrl: string }>
  verify2FA: (code: string) => Promise<void>
  logout: () => void
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null)

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminUser | null>(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const token = adminApi.auth.getToken()
      if (token) {
        const data = await adminApi.auth.me()
        setAdmin(data.admin)
      }
    } catch {
      adminApi.auth.clearToken()
    } finally {
      setLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    const data = await adminApi.auth.login(email, password)
    adminApi.auth.setToken(data.token)
    if (!data.requiresTwoFa && !data.twoFaSetupRequired) {
      const meData = await adminApi.auth.me()
      setAdmin(meData.admin)
    }
    return { requiresTwoFa: data.requiresTwoFa, twoFaSetupRequired: data.twoFaSetupRequired }
  }

  const setup2FA = async () => {
    const data = await adminApi.auth.setup2FA()
    return { secret: data.secret, qrCodeUrl: data.qrCodeUrl }
  }

  const verify2FA = async (code: string) => {
    const data = await adminApi.auth.verify2FA(code)
    adminApi.auth.setToken(data.token)
    const meData = await adminApi.auth.me()
    setAdmin(meData.admin)
  }

  const logout = () => {
    adminApi.auth.clearToken()
    setAdmin(null)
    queryClient.clear()
  }

  return (
    <AdminAuthContext.Provider value={{ admin, loading, login, setup2FA, verify2FA, logout }}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider')
  return ctx
}
